"use client";

import Image from "next/image";

/* ── images split across two rows ── */
const ROW_TOP = [
  "/social/IMG_2966.jpg",
  "/social/IMG_2967.jpg",
  "/social/IMG_2968.jpg",
  "/social/IMG_2969.jpg",
  "/social/IMG_2970.jpg",
  "/social/IMG_2971.jpg",
  "/social/IMG_2972.jpg",
  "/social/IMG_2973.jpg",
  "/social/IMG_2974.jpg",
  "/social/IMG_2975.jpg",
  "/social/IMG_2976.jpg",
  "/social/IMG_2977.jpg",
  "/social/IMG_2978.jpg",
  "/social/IMG_2979.jpg",
  "/social/IMG_2980.jpg",
  "/social/IMG_2981.jpg",
  "/social/IMG_2982.jpg",
  "/social/IMG_2983.jpg",
];

const ROW_BOTTOM = [
  "/social/IMG_2984.jpg",
  "/social/IMG_2985.jpg",
  "/social/IMG_2986.jpg",
  "/social/IMG_2987.jpg",
  "/social/IMG_2988.jpg",
  "/social/IMG_2989.jpg",
  "/social/IMG_2990.jpg",
  "/social/IMG_2991.jpg",
  "/social/IMG_2992.jpg",
  "/social/IMG_2993.jpg",
  "/social/IMG_2994.jpg",
  "/social/IMG_2995.jpg",
  "/social/IMG_2996.jpg",
  "/social/IMG_2997.jpg",
  "/social/IMG_2998.jpg",
  "/social/IMG_2999.jpg",
  "/social/IMG_3001.jpg",
];

const ITEM_SIZE = 130;
const GAP = 10;
const SPEED_TOP = 220;
const SPEED_BOTTOM = 240;

function CarouselRow({
  images,
  direction,
  speed,
}: {
  images: string[];
  direction: "left" | "right";
  speed: number;
}) {
  const doubled = [...images, ...images];
  const totalWidth = images.length * (ITEM_SIZE + GAP);
  const animName = direction === "left" ? "social-scroll-left" : "social-scroll-right";

  return (
    <div className="overflow-hidden w-full">
      <style>{`
        @keyframes social-scroll-left { from { transform: translateX(0); } to { transform: translateX(-${totalWidth}px); } }
        @keyframes social-scroll-right { from { transform: translateX(-${totalWidth}px); } to { transform: translateX(0); } }
      `}</style>
      <div
        className="flex will-change-transform"
        style={{
          gap: GAP,
          width: totalWidth * 2 + GAP,
          animation: `${animName} ${speed}s linear infinite`,
        }}
      >
        {doubled.map((src, i) => (
          <div
            key={`${src}-${i}`}
            className="shrink-0 rounded-lg overflow-hidden bg-gray-100"
            style={{ width: ITEM_SIZE, height: ITEM_SIZE }}
          >
            <Image
              src={src}
              alt="Ungdomskraft på sociala medier"
              width={260}
              height={260}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── social icons ── */
function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function TiktokIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

import type { Locale } from "./templates/ServicePageTemplate";

export default function SocialFeed({ locale = "sv" }: { locale?: Locale }) {
  return (
    <section className="py-8 sm:pt-10 sm:pb-24 bg-white overflow-hidden">
      <div className="relative">
        {/* Carousel rows stacked with small gap */}
        <div className="flex flex-col gap-2.5">
          <CarouselRow images={ROW_TOP} direction="left" speed={SPEED_TOP} />
          <CarouselRow images={ROW_BOTTOM} direction="right" speed={SPEED_BOTTOM} />
        </div>

        {/* Edge fade — left */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-32 sm:w-52 bg-gradient-to-r from-white to-transparent" />
        {/* Edge fade — right */}
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-32 sm:w-52 bg-gradient-to-l from-white to-transparent" />

        {/* Center overlay — "Följ oss" badge */}
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto bg-white/85 backdrop-blur-md rounded-xl px-7 py-3 sm:px-8 sm:py-3.5 shadow-lg shadow-black/5 border border-gray-100">
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 text-center">
              {locale === "en" ? "Follow us" : "Följ oss"}
            </p>
            <div className="mt-1.5 flex items-center justify-center gap-5">
              <a
                href="https://instagram.com/ungdomskraft.co"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-dark hover:text-brand-secondary transition-colors"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
              <a
                href="https://facebook.com/ungdomskraft"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-dark hover:text-brand-secondary transition-colors"
                aria-label="Facebook"
              >
                <FacebookIcon />
              </a>
              <a
                href="https://tiktok.com/@ungdomskraft"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-dark hover:text-brand-secondary transition-colors"
                aria-label="TikTok"
              >
                <TiktokIcon />
              </a>
              <a
                href="https://www.linkedin.com/company/ungdomskraft-ab/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-dark hover:text-brand-secondary transition-colors"
                aria-label="LinkedIn"
              >
                <LinkedInIcon />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
